import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { useLessons } from 'components/main/LessonsProvider';
import _intersection from 'lodash/intersection';
import _isEmpty from 'lodash/isEmpty';
import { Category, GradeLevel, Platform, Subject, Technology } from 'models';
import Lesson from 'models/Lesson';
import React, { ChangeEvent, useEffect, useState } from 'react';
import Approval from '../../../models/Approval';
import ResourceCards from '../ResourceCards';
import Filters from './components/Filters';
import NoResults from './components/NoResults';
import { Filter, filters } from './models/filters';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    paddingTop: 40,
  },
}));

export interface FilterState {
  selectedGradeLevel: GradeLevel[];
  selectedSubject: Subject | '';
  selectedCategory: Category | '';
  selectedPlatform: Platform | '';
  selectedTechnology: Technology | '';
  selectedApproval: Approval | '';
}

interface FilterStateMap {
  gradeLevels: FilterState['selectedGradeLevel'];
  subjectAreas: FilterState['selectedSubject'];
  categories: FilterState['selectedCategory'];
  platforms: FilterState['selectedPlatform'];
  technologies: FilterState['selectedTechnology'];
  submissionApproved: FilterState['selectedApproval'];
}

const Search: React.FC = () => {
  const classes = useStyles();
  const allLessons = useLessons();

  const [filteredLessons, setFilteredLessons] = useState<Lesson[]>(allLessons);
  const [filterState, setFilterState] = useState<FilterState>({
    selectedGradeLevel: [],
    selectedSubject: '',
    selectedCategory: '',
    selectedPlatform: '',
    selectedTechnology: '',
    selectedApproval: '',
  });

  useEffect(() => {
    const filterStateMap = {
      gradeLevels: filterState.selectedGradeLevel,
      subjectAreas: filterState.selectedSubject,
      categories: filterState.selectedCategory,
      platforms: filterState.selectedPlatform,
      technologies: filterState.selectedTechnology,
      submissionApproved: filterState.selectedApproval,
    };

    let lessonsToDisplay = allLessons;

    Object.values(filters).map((filter: Filter) => {
      return (lessonsToDisplay = lessonsToDisplay.filter((lesson) => {
        const filterName = filter.name as keyof FilterStateMap;
        const lessonFieldValue = lesson[filter.name] as boolean | string[];
        const selectedFilterValues = filterStateMap[filterName];

        // If user hasn't filtered on this field...
        if (_isEmpty(selectedFilterValues)) {
          return true;
        }

        // If that field of the lesson is empty...
        if (lessonFieldValue === null) {
          return false;
        }

        // If the filter allows multiple selections (i.e.- grade level)...
        if (filter.isMultivalue) {
          return _intersection(lessonFieldValue as string[], selectedFilterValues).length > 0;
        }

        // If the user has filtered on 'Other'...
        if ((selectedFilterValues as string) === 'Other') {
          return (lessonFieldValue as string[]).some(
            (value: string) => value === 'Other' || !Object.values(filter.model).includes(value),
          );
        }

        // If it's an array value, check that the selected value is included in it
        if (Array.isArray(lessonFieldValue)) {
          return lessonFieldValue.includes(selectedFilterValues as string);
        }
        // If it's not an array, we just compare the value directly
        return String(lessonFieldValue) === selectedFilterValues;
      }));
    });

    return setFilteredLessons(lessonsToDisplay);
  }, [allLessons, filterState]);

  function handleMultivalueChange(values: string[]) {
    const valueCount = values.length;

    // if "All" was the most recent option selected, clear all filters for this field
    if (values[valueCount - 1] === '') {
      return [];
    }

    // if another value was selected after "All", deselect "All" from filters
    if (valueCount > 1) {
      return values.filter((value) => value !== '');
    }
    return values;
  }

  function handleChange(event: ChangeEvent<{ name: string; value: unknown }>) {
    switch (event.target.name) {
      case 'gradeLevel':
        const filteredValues = handleMultivalueChange(event.target.value as string[]);
        setFilterState({ ...filterState, selectedGradeLevel: filteredValues as GradeLevel[] });
        break;
      case 'subject':
        setFilterState({ ...filterState, selectedSubject: event.target.value as Subject });
        break;
      case 'category':
        setFilterState({ ...filterState, selectedCategory: event.target.value as Category });
        break;
      case 'technology':
        setFilterState({ ...filterState, selectedTechnology: event.target.value as Technology });
        break;
      case 'platform':
        setFilterState({ ...filterState, selectedPlatform: event.target.value as Platform });
        break;
      case 'approval':
        setFilterState({ ...filterState, selectedApproval: event.target.value as Approval });
        break;
    }
  }

  return (
    <Grid container spacing={4}>
      <Grid container item xs={12} md={3} alignContent="flex-start" spacing={1}>
        <Filters filterState={filterState} handleChange={handleChange} />
      </Grid>
      <Grid item xs={12} md={9}>
        <div className={classes.cardContainer}>
          {filteredLessons.length > 0 ? <ResourceCards lessons={filteredLessons} /> : <NoResults />}
        </div>
      </Grid>
    </Grid>
  );
};

export default Search;
